import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { FLEET_ACTIVE_OPTIONS, FleetTrailerType } from '../models/trailer.model';
import { FleetTrailerService } from '../trailer.service';

@Component({
    selector: 'app-trailer-type-form',
    standalone: true,
    templateUrl: './trailer-type-form.component.html',
    styleUrls: ['../trailer-page.css'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        InputNumberModule,
        InputTextModule,
        SelectModule,
        TextareaModule,
        ToastModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent
    ]
})
export class TrailerTypeFormComponent implements OnInit {
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    form!: FormGroup;
    trailerTypeId?: number;
    existing?: FleetTrailerType;
    isLoading = false;
    isSaving = false;
    readonly activeOptions = FLEET_ACTIVE_OPTIONS;

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private trailerService: FleetTrailerService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.trailerTypeId = Number(this.route.snapshot.paramMap.get('id')) || undefined;
        this.breadcrumbItems = [
            { label: 'Fleet', routerLink: '/fleet' },
            { label: 'Trailer Types', routerLink: '/fleet/trailer-types' },
            { label: this.trailerTypeId ? 'Edit' : 'Create' }
        ];
        this.form = this.fb.group({
            code: ['', Validators.required],
            name: ['', Validators.required],
            description: [''],
            active: [true, Validators.required],
            sortOrder: [0]
        });

        if (this.trailerTypeId) {
            this.loadTrailerType(this.trailerTypeId);
        }
    }

    save(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.messageService.add({ severity: 'warn', summary: 'Missing details', detail: 'Code and name are required.' });
            return;
        }

        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Company missing', detail: 'No owning company is available in session.' });
            return;
        }

        this.isSaving = true;
        const payload: FleetTrailerType = {
            ...this.existing,
            ...this.form.value,
            trailerTypeId: this.trailerTypeId,
            owningCompanyId: companyId
        };
        const request = this.trailerTypeId
            ? this.trailerService.updateTrailerType(this.trailerTypeId, payload)
            : this.trailerService.createTrailerType(payload);

        request.subscribe({
            next: response => {
                this.isSaving = false;
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Trailer type saved.' });
                this.router.navigate(['/fleet/trailer-type/view', response.data.trailerTypeId]);
            },
            error: error => {
                this.isSaving = false;
                this.messageService.add({ severity: 'error', summary: 'Save failed', detail: error?.error?.message || 'Could not save trailer type.' });
            }
        });
    }

    cancel(): void {
        if (this.trailerTypeId) {
            this.router.navigate(['/fleet/trailer-type/view', this.trailerTypeId]);
            return;
        }
        this.router.navigate(['/fleet/trailer-types']);
    }

    title(): string {
        return this.trailerTypeId ? 'Edit Trailer Type' : 'New Trailer Type';
    }

    private loadTrailerType(id: number): void {
        this.isLoading = true;
        this.trailerService.getTrailerType(id).subscribe({
            next: response => {
                this.existing = response.data;
                this.form.patchValue({
                    code: response.data.code ?? '',
                    name: response.data.name ?? '',
                    description: response.data.description ?? '',
                    active: response.data.active !== false,
                    sortOrder: response.data.sortOrder ?? 0
                });
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load trailer type.' });
            }
        });
    }
}
